import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Plus, Archive, LoaderCircle } from 'lucide-react'

import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import WorkflowTemplateDialog from './WorkflowTemplateDialog'

interface ArchiveConfirmDialogProps {
  templateId: string
  onCancel: () => void
  onArchived: () => void
}

/**
 * Mirrors SubmittalReviewsCard's VerdictConfirmDialog — mounted only while a
 * template's archive button was just clicked, owns the mutation itself and
 * reports back on success rather than letting `window.confirm` gate it.
 */
const ArchiveConfirmDialog: React.FC<ArchiveConfirmDialogProps> = ({ templateId, onCancel, onArchived }) => {
  const { t } = useTranslation()
  const [archiveTemplate, { loading: archiving }] = useMutation(Mutation.ARCHIVE_SUBMITTAL_WORKFLOW_TEMPLATE)

  const handleClose = () => {
    if (archiving) return
    onCancel()
  }

  const handleConfirm = async () => {
    try {
      await archiveTemplate({ variables: { id: templateId } })
      toastr.success('', t('Submittals_wf_archived'))
      onArchived()
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    }
  }

  return (
    <Dialog open onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('Submittals_wf_archive_confirm')}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={archiving}>
            {t('Common_cancel')}
          </Button>
          <Button type="button" variant="destructive" onClick={handleConfirm} disabled={archiving}>
            {archiving && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Submittals_wf_archive')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface WorkflowTemplatesTabProps {
  projectId: string
}

/**
 * ADMIN-only template list — reached only through SubmittalLookupsDialog's
 * second tab, which is itself only opened from the `canAdmin` toolbar button in
 * pages/submittals/index.tsx, so no additional permission check is needed here.
 *
 * The tab holds the LIST only. Editing happens in WorkflowTemplateDialog, a
 * separate wide dialog: the visual flow editor cannot be squeezed into the
 * lookups dialog's `sm:max-w-2xl` shell, and a nested dialog keeps the list
 * visible underneath instead of swapping the tab's content in place.
 */
const WorkflowTemplatesTab: React.FC<WorkflowTemplatesTabProps> = ({ projectId }) => {
  const { t } = useTranslation()
  const [includeArchived, setIncludeArchived] = useState(false)
  // `{ template: null }` is the "new template" case — distinct from `null`,
  // which means no dialog is open at all.
  const [editing, setEditing] = useState<{ template: any | null } | null>(null)
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null)

  const { data, loading, refetch } = useQuery(Query.SUBMITTAL_WORKFLOW_TEMPLATES, {
    variables: { projectId, includeArchived },
    fetchPolicy: 'cache-and-network',
    skip: !projectId,
  })
  const { data: membersData } = useQuery(Query.PROJECT_MEMBERS, {
    variables: { projectId },
    skip: !projectId,
  })

  const templates: any[] = data?.submittalWorkflowTemplates || []
  const members = (membersData?.project?.users ?? []).map((m: any) => m.user).filter(Boolean)

  const handleSaved = async () => {
    await refetch()
    setEditing(null)
  }

  const handleArchived = async () => {
    await refetch()
    setArchiveConfirmId(null)
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id="wf-templates-show-archived"
            checked={includeArchived}
            onCheckedChange={(checked) => setIncludeArchived(!!checked)}
          />
          <Label htmlFor="wf-templates-show-archived" className="cursor-pointer text-sm font-normal">
            {t('Submittals_wf_show_archived')}
          </Label>
        </div>

        <Button type="button" size="sm" className="h-8" onClick={() => setEditing({ template: null })}>
          <Plus className="h-4 w-4" />
          {t('Submittals_wf_new_template')}
        </Button>
      </div>

      <div className="max-h-96 space-y-1.5 overflow-y-auto pr-1">
        {loading && templates.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">{t('Common_loading')}</div>
        ) : templates.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">{t('Submittals_wf_no_templates')}</div>
        ) : (
          templates.map((tpl) => (
            <div
              key={tpl.id}
              onClick={() => setEditing({ template: tpl })}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded border px-3 py-2 transition-colors hover:border-primary/60',
                tpl.archived && 'bg-muted/40',
              )}
            >
              <span
                className={cn(
                  'flex-1 truncate text-sm',
                  tpl.archived && 'text-muted-foreground line-through',
                )}
              >
                {tpl.name}
              </span>
              <Badge variant="secondary" className="shrink-0">
                {tpl.steps.length}
              </Badge>
              {!tpl.archived && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 shrink-0 p-0"
                  title={t('Submittals_wf_archive')}
                  onClick={(e) => {
                    e.stopPropagation()
                    setArchiveConfirmId(tpl.id)
                  }}
                >
                  <Archive className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))
        )}
      </div>

      {editing && (
        <WorkflowTemplateDialog
          projectId={projectId}
          template={editing.template}
          members={members}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {archiveConfirmId && (
        <ArchiveConfirmDialog
          templateId={archiveConfirmId}
          onCancel={() => setArchiveConfirmId(null)}
          onArchived={handleArchived}
        />
      )}
    </div>
  )
}

export default WorkflowTemplatesTab
